import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { REVIEWS, GOOGLE_REVIEWS } from "../data/content";

const Stars = ({ className = "", style = {} }) => (
    <span
        aria-label="5 din 5 stele"
        className={className}
        style={{ letterSpacing: "0.28em", color: "var(--accent)", ...style }}
    >
        ★★★★★
    </span>
);

export default function Testimonials() {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const touchX = useRef(null);
    const count = REVIEWS.length;

    const go = useCallback(
        (i) => setIndex((i + count) % count),
        [count],
    );

    useEffect(() => {
        if (paused) return undefined;
        const id = setInterval(() => setIndex((p) => (p + 1) % count), 7000);
        return () => clearInterval(id);
    }, [paused, count]);

    const onTouchStart = (e) => {
        touchX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? index + 1 : index - 1);
        touchX.current = null;
    };

    const review = REVIEWS[index];

    return (
        <section
            id="recenzii"
            data-testid="testimonials"
            className="px-6 py-36 md:px-12 md:py-56"
            style={{ backgroundColor: "var(--bg)" }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <div className="mx-auto max-w-4xl text-center">
                <Reveal>
                    <span className="eyebrow">Ce spun clientele noastre</span>
                </Reveal>

                <Reveal delay={0.08}>
                    <div className="mt-8">
                        <Stars className="text-sm" />
                    </div>
                </Reveal>

                {/* Editorial quote — slow fade */}
                <div className="mt-12 flex items-center justify-center md:mt-16 md:min-h-[300px]">
                    <AnimatePresence mode="wait">
                        <motion.blockquote
                            key={index}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                            data-testid={`review-${index}`}
                        >
                            <p
                                className="font-serif font-light leading-[1.18]"
                                style={{
                                    fontSize: "clamp(1.7rem, 3.6vw, 3.2rem)",
                                }}
                            >
                                „{review.quote}”
                            </p>
                            <footer className="mt-10">
                                <span className="font-serif text-xl italic">
                                    {review.name}
                                </span>
                                <span
                                    className="eyebrow mt-3 block"
                                    style={{ color: "var(--accent)" }}
                                >
                                    Google Reviews
                                </span>
                            </footer>
                        </motion.blockquote>
                    </AnimatePresence>
                </div>

                {/* Minimal indicator: ←  01 / 03  →  (arrows appear on hover) */}
                <div className="group mt-16 flex items-center justify-center gap-6">
                    <button
                        type="button"
                        onClick={() => go(index - 1)}
                        data-testid="review-prev"
                        aria-label="Recenzia anterioară"
                        className="eyebrow cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity duration-500 hover:!opacity-100 group-hover:opacity-60"
                        style={{ color: "var(--ink)" }}
                    >
                        ←
                    </button>
                    <span className="eyebrow" style={{ color: "var(--ink)" }}>
                        0{index + 1}
                        <span style={{ color: "var(--muted-foreground)" }}>
                            {" "}
                            / 0{count}
                        </span>
                    </span>
                    <button
                        type="button"
                        onClick={() => go(index + 1)}
                        data-testid="review-next"
                        aria-label="Recenzia următoare"
                        className="eyebrow cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity duration-500 hover:!opacity-100 group-hover:opacity-60"
                        style={{ color: "var(--ink)" }}
                    >
                        →
                    </button>
                </div>

                {/* Discreet aggregate line */}
                <Reveal delay={0.05}>
                    <p
                        className="eyebrow mt-10"
                        style={{ color: "var(--muted-foreground)" }}
                    >
                        ★★★★★ 5.0 • Peste 100 de recenzii pe Google, Facebook și
                        Instagram
                    </p>
                </Reveal>

                {/* Link to all reviews */}
                <Reveal delay={0.1}>
                    <a
                        href={GOOGLE_REVIEWS}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="all-reviews-link"
                        className="link-underline group mt-12 inline-flex items-center gap-2 font-sans text-sm"
                    >
                        Vezi toate recenziile
                        <span className="transition-transform duration-500 group-hover:translate-x-1">
                            →
                        </span>
                    </a>
                </Reveal>
            </div>
        </section>
    );
}
